import { CSSProperties, MutableRefObject, useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { colors, fonts,  } from "../../../Styles"
import { NewSelect, ProgressBar, } from "../.."
import { penTypes, NewFieldStyles, NewFieldTypes } from '../../../Definitions';
import { LfLocation } from '../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall';
import { useAppSelector } from '../../../Store';
import { Graph_QL } from '../../../Services';
import { PanelMaintainAnimal } from '../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall';
import { keyBy, merge } from 'lodash';

// TODO
import useSelectOptions from '../../_Components/useSelectOptions';
import FeedCorrectionPopover from '../feedCorrectionPopover';
import { coerceInputValue } from 'graphql';

interface PenEdit_Props {
  pen?: LfLocation
  penType: penTypes
  onCheck?: (isChecked: boolean) => void
  isChecked?: boolean
  borderRadius?: CSSProperties
  dirtyDataRef: MutableRefObject<PanelMaintainAnimal[]>
}
/**
 * PEN_EDIT
 * @param penId Unique identifier for each pen.
 * @param penType Basic, advanced and single. Each renders different content of animal data.
 * @returns a editable pen with varying content of animal data for Freeda Location Feeder systems.
 */
export default function PEN_EDIT({ pen, penType, onCheck, isChecked, borderRadius, dirtyDataRef }: PenEdit_Props) {
  const { t } = useTranslation()
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const { feedcurveOptions } = useSelectOptions();
  
  const animalInfo = useMemo(() => {
    const animal = pen?.animals && pen.animals.length > 0 ? pen?.animals[0] : undefined
    const currentAnimal = dirtyDataRef.current.find(a => a.animalId === animal?.animal_id);

    // Milestone Day
    const milestoneDayNo = animal?.milestone_day
    ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24))
    : t("noData", {defaultValue:"no data"});

    // Milestone Name TODO
    const milestoneName = animal?.milestone ? animal.milestone.name : "";

    if (animal) {
      dirtyDataRef.current = Object.values(
        merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{
          ...currentAnimal,
          animalId: animal?.animal_id,
          feedCurveId: animal?.feedCurve?.feedCurveId,
          correction: animal?.correction ?? null,
        }], "animalId"))
      );
    }

    return {
      animal,
      currentAnimal,
      milestoneDayNo,
      milestoneName,
    }
  }, [dirtyDataRef, pen?.animals, t])

  // ConsumptionProgressBar
  const ConsumptionProgressBar: JSX.Element = useMemo(() => {
    const progress = (animalInfo.animal?.consumptions?.[0])
    const progressBarTotal = (progress?.total ?? 0) / 1000;
    const progressBarProgressed = (progress?.progressed ?? 0) / 1000;

    return (
      <ProgressBar
        total={progressBarTotal}
        progressed={+(progressBarTotal - progressBarProgressed).toFixed(2)}
      />
    );
  }, [animalInfo.animal?.consumptions])

  // FeedCorrection
  const FeedCorrection: JSX.Element = useMemo(() => {
    return (
      <FeedCorrectionPopover
        defaultValue={animalInfo.animal?.correction}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary,
          fieldType: NewFieldTypes.tertiaryField
        }}
        onChange={(selectedValue) => {
          dirtyDataRef.current = Object.values(merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{animalId: animalInfo.animal?.animal_id, correction: selectedValue}], "animalId")))
        }}
      />
    );
  }, [animalInfo.animal?.animal_id, animalInfo.animal?.correction, dirtyDataRef])
  
  // FeedCurve
  const FeedCurve: JSX.Element = useMemo(() => {
    const animalFeedCurve = animalInfo.animal?.feedCurve

    return (
      <NewSelect
        onChange={(selectedValue) => {
          dirtyDataRef.current = Object.values(merge({}, keyBy(dirtyDataRef.current, "animalId"), keyBy([{animalId: animalInfo.animal?.animal_id, feedCurveId: selectedValue?.id}], "animalId")))
        }}
        options={feedcurveOptions} 
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary, 
          fieldType: NewFieldTypes.tertiaryField, 
          fieldWidth: "100%",
          menuWidth: "280px"
        }}  
        valueOverrides={{
          defaultValue: animalFeedCurve ? {label: animalFeedCurve?.name, id: animalFeedCurve?.feedCurveId} : undefined
        }}
      />
    )
  }, [animalInfo.animal, feedcurveOptions, dirtyDataRef])




  // #region | Content
  // Advanced content to parse in the div
  const PenTypes = () => {
    switch (penType) {
      case penTypes.advanced:
      return ( <>
        {animalInfo.animal ?
          <div style={{ display: "grid", gridTemplateColumns: "22px 45px 45px 108px 45px 108px 45px 100px 45px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
            <span></span> {/* <CheckBox checkStyle={CheckStyles.transparent} isChecked={isChecked} onChange={(checked) => { if (onCheck) { onCheck(checked); }}}/> */}
            <span style={{ cursor: "default" }}>{pen?.name}</span>
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{animalInfo.animal?.animal_number}</span>
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{animalInfo.milestoneName}</span>
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{animalInfo.milestoneDayNo}</span>
            {FeedCurve}
            {FeedCorrection}
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{ConsumptionProgressBar}</span>
            <span></span> {/* <FeedSkipPopover pen={pen}/> */}
            <span></span>
          </div>
          :
          <div style={{ display: "grid", gridTemplateColumns: "22px 45px 521px 22px", textAlign: "start", alignItems: "center", gap: "5px" }}>
            <span></span>
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{pen?.name}</span>
            <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{t("locationFeederPages.sections.emptyPen", { defaultValue: "Empty pen" })}</span>
            <span></span>
          </div>
        }
      </> );
      case penTypes.simple:
        return (<>
          {animalInfo.animal ?
            <div style={{ display: "grid", gridTemplateColumns: "108px 45px 45px 417px", textAlign: "start", alignItems: "center", gap: "5px" }}>
              {FeedCurve}
              {FeedCorrection}
              <span></span> {/* <FeedSkipPopover pen={pen}/> */}
              <span style={{ filter: "opacity(0.5)", cursor: "default" }}>{ }</span>
            </div>
            :
            <></>
          }
        </>);
      default:
        return null;
    }
  }  
  // #endregion

  return (
    <div style={{
      width: "650px",
      height: "35px",
      boxSizing: "border-box"
    }}>
      <div style={{
        width: "100%",
        height: "100%",
        textAlign: 'center',
        alignContent: "center",
        boxSizing: "border-box",
        padding: "0px 10px",
        backgroundColor: colors.greyLightSecondary,
        transition: 'filter 0.3s',
        color: colors.greyDarkPrimary,
        ...fonts.paragraph,
        ...fonts.fontSize4,
        ...borderRadius
      }}>
        {PenTypes()}
      </div>
    </div>
  );
}